import Loader from "components/DesignSystem/Loader/loader";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import Modal from "components/DesignSystem/Modal/Modal";
import { REGISTERED_AGENT } from "constants/addons";
import { RA_STATE } from "constants/billing";
import { US_UUID } from "constants/regAgent";
import { Form, Formik } from "formik";
import { useAppSelector } from "hooks/useAppSelector";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import { MultiValue, SingleValue } from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { useAddProductToCartMutation } from "store/apis/products";
import { useGetSubscriptionsQuery } from "store/apis/subscriptions";
import { useGetCountryStatesQuery } from "store/apis/teamSetting";
import {
  setAddonPayload,
  setAddonType,
  setOpenCheckoutModal,
} from "store/slices/addons";
import { setPaymentTitle, setUsableCredit } from "store/slices/credit";
import { RootState } from "store/store";
import { GroupSubscriptionState } from "types/Models/subscription";
import { BackendError } from "types/utils/error";

type StateOption = OptionData & { amount: string; uuid: string };

export const RegisteredAgent = () => {
  const { addonData } = useSelector((state: RootState) => state.addonsSlice);
  const [showAddonModal, setShowAddonModal] = useState<boolean>(
    addonData?.subscription_type === REGISTERED_AGENT
  );
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const dispatch = useDispatch();
  const { successToast, alertToast } = useToast();
  const {
    data: states,
    isLoading,
    isFetching,
    isSuccess,
  } = useGetCountryStatesQuery({
    countryId: US_UUID,
  });
  const {
    subscription_name,
    billing_cycle,
    group_subscription,
    selectedState,
    disableStateChange,
  } = addonData || {};
  const { update } = useUpdateQuery();
  const query = useQuery();
  const isSubscriptionOpen = query.get("subscription_to_open");

  const isCartSubscriptionActive = useAppSelector(
    (state) => state.cartSubscription.isCartSubscriptionActive
  );

  const [addToCart] = useAddProductToCartMutation();

  const { data: subscriptions } = useGetSubscriptionsQuery(
    {
      groupId,
      entityId,
    },
    { skip: !groupId || !entityId }
  );

  const handleAddonNext = async (values: { selectedStates: StateOption[] }) => {
    const { selectedStates } = values;

    if (!selectedStates.length) return;

    // Cart mode: add every selected state to the cart (one request per state).
    if (isCartSubscriptionActive && groupId) {
      try {
        await Promise.all(
          selectedStates.map((state) => {
            const subscription = subscriptions?.find(
              (s) => s.uuid === state.uuid
            );

            if (!subscription) {
              throw new Error(`No subscription found for ${state.value}`);
            }

            return addToCart({
              groupId,
              entityId,
              payload: {
                product_id: state.uuid,
                ra_state_name: state.value,
                product_content_type_id: subscription.product_content_type_id,
              },
            }).unwrap();
          })
        );

        successToast({ title: "Added to cart!" });
        dispatch(setAddonType(""));
        setShowAddonModal(false);
      } catch (e) {
        const error = e as BackendError;
        alertToast({
          message:
            error?.data?.error?.message ||
            "Failed to add one or more states to the cart",
        });
      }

      return;
    }

    // Pay & subscribe mode redirects to a single-subscription checkout, so it
    // uses the first selected state (multi-state is a cart-only feature).
    const [selectedState] = selectedStates;

    update({
      query: "subscription_to_open",
      value: null,
    });
    dispatch(
      setAddonPayload({
        title: addonData?.subscription_name,
        amount: selectedState.amount,
        selectedState: selectedState.value,
        uuid: selectedState.uuid,
        type: addonData?.subscription_type,
      })
    );
    dispatch(setUsableCredit(false));
    dispatch(
      setPaymentTitle(
        `${addonData?.subscription_name!} - ${selectedState.value} `
      )
    );
    dispatch(setAddonType(""));
    dispatch(setOpenCheckoutModal());
    setShowAddonModal(false);
  };

  const subscribedStatesFromAddon =
    group_subscription?.map(
      (state: GroupSubscriptionState) => state.ra_state_name
    ) || [];

  const subscribedStatesFromSubscriptions =
    subscriptions
      ?.filter((sub) => sub.subscription_type === "REGISTERED_AGENT")
      ?.flatMap((sub) => sub.group_subscription || [])
      ?.map((gs) => gs.ra_state_name) || [];

  const allSubscribedStates = [
    ...subscribedStatesFromAddon,
    ...subscribedStatesFromSubscriptions,
  ];

  const stateOptions: StateOption[] =
    states?.map((ele: any) => {
      const stateMatch = addonData?.ra_state_amount_maps?.find(
        (state) => state.ra_state === ele.name
      );
      const othersMatch = addonData?.ra_state_amount_maps?.find(
        (state) => state.ra_state === RA_STATE.OTHERS
      );
      const stateMap = stateMatch || othersMatch;

      const amount = stateMap?.amount || addonData?.amount;
      const registeredAgentUuid = stateMap?.uuid || addonData?.uuid;
      const isSubscribed = allSubscribedStates.includes(ele.name);

      return {
        label: ele.name,
        value: ele.name,
        amount: amount?.toString() || "",
        uuid: registeredAgentUuid || "",
        disableOption: isSubscribed,
        data: isSubscribed
          ? "You already have a Registered Agent subscription for this state"
          : undefined,
      };
    }) || [];

  const onClose = () => {
    setShowAddonModal(false);
    dispatch(setAddonType(""));
    if (isSubscriptionOpen) {
      update({
        query: "subscription_to_open",
        value: null,
      });
    }
  };

  const selectedStateDetail = stateOptions.find(
    (state) => state.value === selectedState && !state.disableOption
  );

  const handleStateChange = (
    option: MultiValue<OptionData> | SingleValue<OptionData>,
    setFieldValue: (field: string, value: StateOption[]) => void
  ) => {
    const options = (
      Array.isArray(option) ? option : option ? [option] : []
    ) as StateOption[];

    setFieldValue("selectedStates", options);
  };

  return (
    <Modal.Root open={showAddonModal} onOpenChange={onClose}>
      <Modal.Content useCustomOverlay>
        <Modal.Header>
          <Modal.Title>{subscription_name}</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Formik
          initialValues={{
            selectedStates: selectedStateDetail ? [selectedStateDetail] : [],
          }}
          onSubmit={handleAddonNext}
          enableReinitialize
        >
          {({ values, isSubmitting, setFieldValue }) => {
            const totalAmount = values.selectedStates.reduce(
              (sum, state) => sum + Number(state.amount || 0),
              0
            );

            return (
              <Form className="all:unset">
                <Modal.Body>
                  <Async.Root
                    isEmpty={!states?.length}
                    isLoading={isLoading || isFetching}
                    isSuccess={isSuccess}
                    renderOnLoad={
                      <div className="t-h-40 t-flex t-items-center t-justify-center">
                        <Loader />
                      </div>
                    }
                  >
                    <Async.Empty>
                      <></>
                    </Async.Empty>
                    <Async.Success>
                      <div className="t-flex t-flex-col t-gap-4">
                        <div className="t-text-text-60 t-text-body-sm">
                          Inkle Virtual Registered Agent (RA) is available in
                          all US states as a yearly subscription. Select a state
                          to get started.
                        </div>
                        <div className="t-w-full t-flex t-gap-10 t-items-end">
                          <div className="t-w-[280px]">
                            <Combobox
                              label="State"
                              name="state"
                              placeholder="Select state"
                              isMulti={isCartSubscriptionActive}
                              multiValueShowLimit={2}
                              options={stateOptions}
                              value={
                                isCartSubscriptionActive
                                  ? values.selectedStates
                                  : values.selectedStates[0] ?? null
                              }
                              onChange={(option) =>
                                handleStateChange(option, setFieldValue)
                              }
                              isOptionDisabled={(option) =>
                                Boolean(option.disableOption)
                              }
                              isDisabled={disableStateChange}
                              isClearable={false}
                              tooltip
                              menuPortalTarget={document.body}
                            />
                          </div>
                          <div>
                            <div className="t-text-text-30 t-text-body-sm">
                              Subscription cost
                            </div>
                            <div className="t-text-body-sm">
                              {values.selectedStates.length
                                ? `$${totalAmount}`
                                : "-"}
                            </div>
                          </div>
                        </div>
                      </div>
                    </Async.Success>
                  </Async.Root>
                </Modal.Body>
                <Modal.Footer className="t-flex t-justify-end t-gap-3">
                  <Button type="button" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    customType="primary"
                    isLoading={isSubmitting}
                    disabled={!values.selectedStates.length || isSubmitting}
                  >
                    {isCartSubscriptionActive
                      ? "Add to cart"
                      : "Pay & subscribe"}
                  </Button>
                </Modal.Footer>
              </Form>
            );
          }}
        </Formik>
      </Modal.Content>
    </Modal.Root>
  );
};
